import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../integrations/supabase/client';
import LoginForm from '../components/auth/LoginForm';
import { Loader2 } from 'lucide-react';

const Login: React.FC = () => {
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, isAuthenticated, loading: authLoading } = useAuth();
  
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    const handleAuthRedirect = async () => {
      if (isAuthenticated && !authLoading) {
        // Check user role to determine redirect destination
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const { data: userRoles } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id)
            .in('role', ['super_admin', 'platform_admin', 'customer_admin', 'user_manager'])
            .order('role')
            .limit(1)
            .maybeSingle();

          const role = userRoles?.role;
          
          // Route based on highest privilege role
          if (role === 'super_admin' || role === 'platform_admin') {
            navigate('/admin');
            return;
          } else if (role === 'customer_admin' || role === 'user_manager') {
            navigate('/portal');
            return;
          }
        }
        
        // Default redirect for regular users or if no role found
        const redirect = searchParams.get('redirect') || '/onboarding';
        navigate(redirect);
      }
    };

    handleAuthRedirect();
  }, [isAuthenticated, authLoading, navigate, searchParams]);

  const handleLogin = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const { error: signInError } = await signIn(email, password);

      if (signInError) {
        throw new Error(signInError.message);
      }

      // Get current user for routing
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Fetch role with timeout to prevent hanging
        let role: string | null = null;
        try {
          const rolePromise = supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id)
            .in('role', ['super_admin', 'platform_admin', 'customer_admin', 'user_manager'])
            .order('role')
            .limit(1)
            .maybeSingle();
          
          // Resolve to null (not reject) to avoid unhandled promise rejections.
          const timeoutPromise = new Promise<null>((resolve) =>
            setTimeout(() => resolve(null), 3000)
          );

          const result = (await Promise.race([rolePromise, timeoutPromise])) as Awaited<typeof rolePromise> | null;

          if (!result) {
            throw new Error('Role fetch timeout');
          }
          if (result.error) {
            throw result.error;
          }

          role = result.data?.role || null;
        } catch (roleError) {
          console.warn('Role fetch failed, using default redirect:', roleError);
        }
        
        // Route based on highest privilege role
        if (role === 'super_admin' || role === 'platform_admin') {
          navigate('/admin');
        } else if (role === 'customer_admin' || role === 'user_manager') {
          navigate('/portal');
        } else {
          navigate('/onboarding');
        }
      } else {
        navigate('/onboarding');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted px-4 sm:px-6 py-8 sm:py-12">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-card rounded border border-border p-6 sm:p-8">
          {/* Logo */}
          <div className="mb-6 sm:mb-8">
            <img 
              src="/faces_black_logo.png" 
              alt="FACES Logo" 
              className="h-6 w-auto"
            />
          </div>

          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-xl sm:text-2xl font-semibold text-foreground mb-2">
              Sign in to your account
            </h1>
            <p className="text-muted-foreground text-sm">
              Sign in to access your FACES client portal
            </p>
          </div>

          {/* Login Form */}
          <LoginForm 
            onSubmit={handleLogin} 
            error={error}
            isLoading={isLoading}
          />

          {/* New User Notice */}
          <div className="mt-6 p-3 sm:p-4 bg-info/5 border border-info/20 rounded">
            <p className="text-xs sm:text-sm text-info-foreground">
              <span className="font-medium">New to FACES?</span> If you received an invitation email, please use the link in that email to set up your password and access the onboarding process.
            </p>
          </div>

          {/* Forgot Password */}
          <div className="mt-6">
            <a 
              href="/forgot-password" 
              className="text-sm text-foreground hover:text-foreground/80 font-medium transition-colors"
            >
              Forgot your password?
            </a>
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-card px-2 text-muted-foreground uppercase">No account?</span>
            </div>
          </div>

          {/* Sign Up Link */}
          <div className="text-center">
            <a 
              href="/signup" 
              className="text-sm text-foreground hover:text-foreground/80 font-medium transition-colors"
            >
              Create an account
            </a>
          </div>
        </div>

        {/* Footer Help */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-xs text-muted-foreground">
            Need help?
          </p>
          <a 
            href="mailto:support@facesoutdoors.com" 
            className="text-sm text-foreground hover:text-foreground/80 font-medium transition-colors inline-block"
          >
            support@facesoutdoors.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
